import { useCallback, useEffect, useState } from 'react';

import {
  getPushPermission,
  isPushConfigured,
  PushPermission,
  registerWebPushToken,
  requestPushPermission,
  unregisterWebPushToken,
} from '@/utils/webPush';

/**
 * Stan zgody na powiadomienia + rejestracja tokenu push.
 *
 * Zgodę tylko ODCZYTUJEMY przy wejściu (`Notification.permission` nie pokazuje monitu),
 * a o monit prosimy wyłącznie z kliknięcia użytkownika. Zmiana ustawienia zrobiona
 * ręcznie w przeglądarce dociera tu przez Permissions API, więc komunikat w panelu
 * znika sam, bez przeładowania strony.
 */
export const useWebPushPermission = (userId?: string) => {
  const [permission, setPermission] = useState<PushPermission>(() => getPushPermission());

  useEffect(() => {
    let status: PermissionStatus | null = null;
    let cancelled = false;

    navigator.permissions?.query({ name: 'notifications' as PermissionName })
      .then((result) => {
        if (cancelled) return;
        status = result;
        // eslint-disable-next-line no-param-reassign
        result.onchange = () => setPermission(getPushPermission());
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, []);

  // Token odświeżamy przy każdym wejściu: FCM potrafi go zrotować, a martwy wpis
  // w `fcmTokens` to cicha utrata powiadomień.
  useEffect(() => {
    if (permission !== 'granted' || !userId) return;
    registerWebPushToken(userId).catch(() => undefined);
  }, [permission, userId]);

  const request = useCallback(async () => {
    const result = await requestPushPermission();
    setPermission(result);
    if (result === 'granted' && userId) await registerWebPushToken(userId);
    return result;
  }, [userId]);

  const disable = useCallback(async () => {
    if (userId) await unregisterWebPushToken(userId);
  }, [userId]);

  return {
    permission,
    /** Monit ma sens tylko przy `default` — po „Blokuj" przeglądarka już nie zapyta. */
    canAsk: permission === 'default',
    isBlocked: permission === 'denied',
    isConfigured: isPushConfigured(),
    request,
    disable,
  };
};
